import React from "react";

/**
 * Luxurious prestige medallion — laurel wreath + star + ribbon banner.
 * Pure SVG, gold gradient, subtle pulse glow animation.
 */
export default function PrestigeMedallion({ size = 96, className = "" }) {
  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {/* Outer glow halo */}
      <div
        className="absolute inset-0 rounded-full opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.45) 0%, rgba(212,175,55,0.15) 35%, transparent 70%)",
          filter: "blur(8px)",
          animation: "prestigePulse 3.5s ease-in-out infinite",
        }}
      />

      <svg
        viewBox="0 0 120 120"
        className="relative w-full h-full"
        style={{ filter: "drop-shadow(0 4px 16px rgba(212,175,55,0.5))" }}
      >
        <defs>
          <linearGradient id="goldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="40%" stopColor="#facc15" />
            <stop offset="65%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#92651a" />
          </linearGradient>
          <linearGradient id="goldShine" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fef9c3" />
            <stop offset="50%" stopColor="#fde68a" />
            <stop offset="100%" stopColor="#d4af37" />
          </linearGradient>
          <radialGradient id="centerGlow" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#fef3c7" stopOpacity="1" />
            <stop offset="60%" stopColor="#d4af37" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#78350f" stopOpacity="1" />
          </radialGradient>
        </defs>

        {/* LEFT laurel branch */}
        <g transform="translate(60 65)">
          <path
            d="M 0 0 Q -20 -8 -30 -22 Q -36 -32 -34 -42"
            stroke="url(#goldGrad)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          {/* Leaves left */}
          {[
            { cx: -10, cy: -8, rx: 6, ry: 3, rot: -30 },
            { cx: -18, cy: -16, rx: 6, ry: 3, rot: -45 },
            { cx: -24, cy: -25, rx: 6, ry: 3, rot: -60 },
            { cx: -28, cy: -34, rx: 5, ry: 2.5, rot: -75 },
            { cx: -16, cy: -3, rx: 5, ry: 2.5, rot: 30 },
            { cx: -23, cy: -10, rx: 5, ry: 2.5, rot: 15 },
            { cx: -29, cy: -19, rx: 5, ry: 2.5, rot: -5 },
            { cx: -32, cy: -28, rx: 4, ry: 2, rot: -20 },
          ].map((l, i) => (
            <ellipse
              key={`L${i}`}
              cx={l.cx}
              cy={l.cy}
              rx={l.rx}
              ry={l.ry}
              fill="url(#goldShine)"
              transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
            />
          ))}
        </g>

        {/* RIGHT laurel branch (mirror) */}
        <g transform="translate(60 65) scale(-1 1)">
          <path
            d="M 0 0 Q -20 -8 -30 -22 Q -36 -32 -34 -42"
            stroke="url(#goldGrad)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          {[
            { cx: -10, cy: -8, rx: 6, ry: 3, rot: -30 },
            { cx: -18, cy: -16, rx: 6, ry: 3, rot: -45 },
            { cx: -24, cy: -25, rx: 6, ry: 3, rot: -60 },
            { cx: -28, cy: -34, rx: 5, ry: 2.5, rot: -75 },
            { cx: -16, cy: -3, rx: 5, ry: 2.5, rot: 30 },
            { cx: -23, cy: -10, rx: 5, ry: 2.5, rot: 15 },
            { cx: -29, cy: -19, rx: 5, ry: 2.5, rot: -5 },
            { cx: -32, cy: -28, rx: 4, ry: 2, rot: -20 },
          ].map((l, i) => (
            <ellipse
              key={`R${i}`}
              cx={l.cx}
              cy={l.cy}
              rx={l.rx}
              ry={l.ry}
              fill="url(#goldShine)"
              transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
            />
          ))}
        </g>

        {/* CENTRAL MEDALLION (round) */}
        <circle cx="60" cy="38" r="22" fill="url(#centerGlow)" stroke="url(#goldGrad)" strokeWidth="2.5" />
        <circle cx="60" cy="38" r="17" fill="none" stroke="#92651a" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />

        {/* CENTER STAR */}
        <g transform="translate(60 38)">
          <path
            d="M 0 -12 L 3.5 -3.5 L 12 -3.5 L 5 2 L 7.5 11 L 0 5 L -7.5 11 L -5 2 L -12 -3.5 L -3.5 -3.5 Z"
            fill="url(#goldShine)"
            stroke="#92651a"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          {/* Star highlight */}
          <path
            d="M 0 -12 L 3.5 -3.5 L 0 -2 Z"
            fill="#fef9c3"
            opacity="0.9"
          />
        </g>

        {/* Hanging ribbon */}
        <path
          d="M 50 56 L 45 78 L 52 74 L 50 56 Z"
          fill="url(#goldGrad)"
          stroke="#92651a"
          strokeWidth="0.6"
        />
        <path
          d="M 70 56 L 75 78 L 68 74 L 70 56 Z"
          fill="url(#goldGrad)"
          stroke="#92651a"
          strokeWidth="0.6"
        />
        {/* Ribbon notch */}
        <path
          d="M 45 78 L 52 74 L 50 80 Z"
          fill="#78350f"
          opacity="0.7"
        />
        <path
          d="M 75 78 L 68 74 L 70 80 Z"
          fill="#78350f"
          opacity="0.7"
        />
      </svg>

      <style>{`
        @keyframes prestigePulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50% { transform: scale(1.15); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}
